import React from "react";
import { Box } from "@amzn/awsui-components-react";
import "./LoadingLine.scss";

/**
 * Loading state with a caption.
 *
 * - `align="center"` (default): a stacked block — ring above the caption —
 *   for empty containers/panels waiting on data.
 * - `align="left"`: a compact inline row for use inside dense layouts.
 *
 * Deliberately NOT the Cloudscape `Spinner` / `StatusIndicator type="loading"`:
 * that spinner is a lone arc sweeping around an invisible circle, so the
 * visible fragment always looks off-centre (repeated alignment fixes could
 * not solve this). The ring below has a visible track and rotates about its
 * exact centre.
 */
export const LoadingLine: React.FC<{
  children: React.ReactNode;
  align?: "left" | "center";
}> = ({ children, align = "center" }) => {
  if (align === "center") {
    return (
      <Box padding={{ vertical: "l", horizontal: "s" }} textAlign="center">
        <div role="status" aria-live="polite">
          <span className="loading-ring loading-ring--big" aria-hidden />
          <Box color="text-body-secondary" padding={{ top: "s" }}>
            {children}
          </Box>
        </div>
      </Box>
    );
  }
  return (
    <Box padding={{ vertical: "xs" }}>
      <div
        role="status"
        aria-live="polite"
        style={{ display: "flex", alignItems: "center", gap: "8px" }}
      >
        <span className="loading-ring loading-ring--normal" aria-hidden />
        <Box color="text-body-secondary" display="inline">
          {children}
        </Box>
      </div>
    </Box>
  );
};

export default LoadingLine;
